import React, { useState, useEffect } from 'react';
import { Search, Download, AlertTriangle, Eye } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../components/ui/utils';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { Asset, MaintenanceRecord } from '../../../types/asset';
import { toSydneyDate } from '../../../utils/helpers';

// Utility to calculate days difference
const getDaysDifference = (targetDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize to midnight
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0); // normalize to midnight

    const diffTime = target.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

type MaintenanceViewItem = {
    asset: Asset;
    maintenance: MaintenanceRecord;
    daysDiff: number;
};

const UpcomingOverdueMaintenanceReport: React.FC = () => {
    const navigate = useNavigate();
    const [maintenanceItems, setMaintenanceItems] = useState<MaintenanceViewItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [timeFilter, setTimeFilter] = useState('All (Next 30 Days)');

    useEffect(() => {
        const fetchMaintenanceData = async () => {
            try {
                setLoading(true);
                // Fetch assets with their maintenance records
                const response = await api.get<Asset[]>(`/api/assets?includeMaintenance=true`);

                const items: MaintenanceViewItem[] = [];

                response.forEach(asset => {
                    if (asset.maintenanceRecords && asset.maintenanceRecords.length > 0) {
                        asset.maintenanceRecords.forEach(record => {
                            // We only care about Scheduled or In Progress records for upcoming/overdue
                            if (record.status !== 'Completed' && record.status !== 'Cancelled') {
                                if (record.scheduledDate) {
                                    const daysDiff = getDaysDifference(record.scheduledDate);
                                    items.push({
                                        asset,
                                        maintenance: record,
                                        daysDiff
                                    });
                                }
                            }
                        });
                    }
                });

                // Sort by earliest scheduled date
                items.sort((a, b) => a.daysDiff - b.daysDiff);

                setMaintenanceItems(items);
            } catch (error) {
                console.error('Error fetching maintenance data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMaintenanceData();
    }, []);

    const filteredItems = maintenanceItems.filter(item => {
        const matchesSearch = item.asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.asset.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.maintenance.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.maintenance.vendor && item.maintenance.vendor.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesType = typeFilter === 'All Types' || item.asset.type === typeFilter;

        let matchesTime = true;
        if (timeFilter === 'All (Next 30 Days)') {
            matchesTime = item.daysDiff <= 30; // Included overdue (< 0) and upcoming (<= 30)
        } else if (timeFilter === 'Overdue Only') {
            matchesTime = item.daysDiff < 0;
        } else if (timeFilter === 'Upcoming Only') {
            matchesTime = item.daysDiff >= 0 && item.daysDiff <= 30;
        }

        return matchesSearch && matchesType && matchesTime;
    });

    const totalItemsCount = filteredItems.length;
    const overdueCount = filteredItems.filter(item => item.daysDiff < 0).length;
    const dueSoonCount = filteredItems.filter(item => item.daysDiff >= 0 && item.daysDiff <= 7).length;
    const upcomingCount = filteredItems.filter(item => item.daysDiff > 7 && item.daysDiff <= 30).length;

    const getDaysText = (days: number) => {
        if (days < 0) return `Overdue by ${Math.abs(days)} days`;
        if (days === 0) return 'Due today';
        if (days === 1) return 'Due tomorrow';
        return `Due in ${days} days`;
    };

    const getDaysStyles = (days: number) => {
        if (days < 0) return 'text-red-600 font-bold';
        if (days <= 7) return 'text-orange-600 font-bold';
        return 'text-blue-600 font-medium';
    };

    const downloadCSV = () => {
        const headers = ['Priority', 'Asset Name', 'Asset ID', 'Maintenance Type', 'Description', 'Scheduled Date', 'Days Until/Overdue', 'Assigned User', 'Vendor', 'Est. Cost'];
        const rows = filteredItems.map(({ asset, maintenance, daysDiff }) => {
            const priority = daysDiff < 0 ? 'Overdue' : daysDiff <= 7 ? 'Due Soon' : 'Upcoming';
            return [
                priority,
                asset.name,
                asset.tag,
                maintenance.type,
                maintenance.description || '',
                toSydneyDate(maintenance.scheduledDate),
                getDaysText(daysDiff),
                asset.assignedToUser?.name || 'Unassigned',
                maintenance.vendor || '',
                maintenance.cost ? Number(maintenance.cost).toFixed(2) : ''
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `upcoming-overdue-maintenance-report-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <AlertTriangle size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Upcoming / Overdue Maintenance Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Prevent missed servicing and failures - Compliance & Safety critical</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadCSV}>
                            <Download size={16} />
                            <span>CSV</span>
                        </Button>
                    </div>
                </div>

                {/* Warning Banner IF there are overdue items */}
                {overdueCount > 0 && (
                    <div className="mt-6 bg-red-50 border border-red-100 rounded-2xl p-4 flex items-start gap-3">
                        <AlertTriangle className="text-red-600 mt-0.5" size={20} />
                        <div>
                            <h4 className="text-red-900 font-bold">{overdueCount} maintenance items are overdue</h4>
                            <p className="text-red-700 text-sm mt-0.5">Immediate action required to maintain compliance and asset reliability</p>
                        </div>
                    </div>
                )}

                {/* Filters Row */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative w-full flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search maintenance..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto no-scrollbar">
                        <select
                            className="h-11 px-4 text-center bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full sm:w-40 appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%20%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={typeFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTypeFilter(e.target.value)}
                        >
                            <option>All Types</option>
                            <option>Vehicle</option>
                            <option>Plant & Equipment</option>
                            <option>Tools</option>
                            <option>IT Equipment</option>
                            <option>Mobile Devices</option>
                            <option>Lifting/Slings</option>
                            <option>Furniture</option>
                        </select>
                        <select
                            className="h-11 px-4 text-center bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full sm:w-48 appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%20%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={timeFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTimeFilter(e.target.value)}
                        >
                            <option>All (Next 30 Days)</option>
                            <option>Overdue Only</option>
                            <option>Upcoming Only</option>
                        </select>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Items</p>
                        <h3 className="text-2xl font-black text-gray-900">{totalItemsCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Overdue</p>
                        <h3 className="text-2xl font-black text-red-600">{overdueCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Due Soon <span className="text-[9px] lowercase">(7 days)</span></p>
                        <h3 className="text-2xl font-black text-orange-600">{dueSoonCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Upcoming <span className="text-[9px] lowercase">(8-30 days)</span></p>
                        <h3 className="text-2xl font-black text-blue-600">{upcomingCount}</h3>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto no-scrollbar pb-4">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Priority</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Maintenance Type</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Scheduled Date</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Days Until/Overdue</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assigned User</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Vendor</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Est. Cost</th>
                            <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-800 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan={9} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : filteredItems.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                                    No maintenance records found matching the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredItems.map(({ asset, maintenance, daysDiff }) => {
                                const isOverdue = daysDiff < 0;
                                const isOrange = daysDiff >= 0 && daysDiff <= 7;
                                return (
                                    <tr key={`${asset.id}-${maintenance.id}`} className={cn("hover:bg-gray-50/50 transition-colors group", isOverdue ? "bg-red-50/30" : "")}>
                                        <td className="px-6 py-4">
                                            {isOverdue ? (
                                                <Badge variant="outline" className="bg-red-600 text-white border-red-600 flex items-center gap-1.5 w-fit px-2 py-0.5 whitespace-nowrap">
                                                    <AlertTriangle size={12} fill="currentColor" className="text-red-600 fill-white" />
                                                    <span className="text-[10px]">Overdue</span>
                                                </Badge>
                                            ) : isOrange ? (
                                                <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200 w-fit px-2.5 py-0.5 whitespace-nowrap">
                                                    <span className="text-[10px]">Due Soon</span>
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 w-fit px-2.5 py-0.5 whitespace-nowrap">
                                                    <span className="text-[10px]">Upcoming</span>
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="min-w-[160px]">
                                                <p className="font-bold text-gray-900 text-[13px]">{asset.name}</p>
                                                <p className="text-[11px] font-medium text-gray-400 font-mono tracking-wider mt-0.5">{asset.tag}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="min-w-[150px]">
                                                <p className="font-bold text-gray-900 text-[13px]">{maintenance.type}</p>
                                                <p className="text-[11px] text-gray-500 mt-0.5 truncate max-w-[200px]" title={maintenance.description}>{maintenance.description || 'Routine service'}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-700 text-[13px] whitespace-nowrap">
                                            {toSydneyDate(maintenance.scheduledDate)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={cn("text-[13px] whitespace-nowrap", getDaysStyles(daysDiff))}>
                                                {getDaysText(daysDiff)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {asset.assignedToUser?.name || 'Unassigned'}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {maintenance.vendor || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 font-mono whitespace-nowrap">
                                            {maintenance.cost ? `$${Number(maintenance.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-900 transition-colors"
                                                onClick={() => navigate(`/assets/${asset.id}`)}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UpcomingOverdueMaintenanceReport;
